import React, { useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Sparkles, Award, Users, Clock, Heart } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const features = [
  {
    icon: Award,
    title: 'Premium Quality',
    description: 'High-end products and expert techniques for flawless, long-lasting results.',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    icon: Users,
    title: 'Women & Latino-Owned',
    description: 'Proudly supporting diversity and empowering our community since day one.',
    gradient: 'from-rose-500 to-pink-600',
  },
  {
    icon: Clock,
    title: 'Walk-Ins Welcome',
    description: "No appointment needed — we're ready when you are.",
    gradient: 'from-pink-600 to-fuchsia-500',
  },
  {
    icon: Heart,
    title: 'Luxury Experience',
    description: 'Pink roses, neon ambiance, and spa-grade pampering by the beach.',
    gradient: 'from-fuchsia-500 to-pink-500',
  },
];

export const Features = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current?.children || [], {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: { trigger: headerRef.current, start: 'top 80%' },
      });

      gsap.from(gridRef.current?.querySelectorAll('.feature-tile') || [], {
        y: 60,
        opacity: 0,
        scale: 0.94,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: { trigger: gridRef.current, start: 'top 80%' },
      });

      // Floating icon animation on the cards
      gridRef.current?.querySelectorAll('.feature-icon').forEach((el, i) => {
        gsap.to(el, {
          y: -8,
          duration: 2 + i * 0.3,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="features-section"
      className="py-24 bg-gradient-to-b from-white to-pink-50/30 relative overflow-hidden"
    >
      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-rose-100 px-4 py-2 rounded-full mb-5">
            <Sparkles className="w-4 h-4 text-rose-600" />
            <span className="text-sm font-medium text-rose-700">Why Choose Us</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 bg-gradient-to-r from-rose-500 to-pink-600 bg-clip-text text-transparent leading-tight">
            The Stiletto Experience
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            More than a nail salon — your sanctuary for beauty, glow, and self-care.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                data-testid={`feature-card-${index}`}
                className="feature-tile group border-none shadow-md hover:shadow-2xl hover:shadow-pink-200/50 transition-all duration-500 hover:-translate-y-2 bg-white/80 backdrop-blur-sm rounded-2xl"
              >
                <div className="p-7 text-center">
                  <div
                    className={`feature-icon inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} mb-5 shadow-lg group-hover:scale-110 transition-transform duration-500`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
